Fix<template>
  <div class="kpt-devices-root kpt-list-page">
    <vue-headful title="Devices" />
    <header class="kpt-list-header">
      <h4>Devices</h4>
      <div class="page-control">
        <!-- <font-awesome-icon :icon="['far', 'thumbtack']" @click="pinToDash('deviceList', { name: 'Device List' })" title="Pin to Dashboard" class="has-cursor-pointer" /> -->
      </div>
    </header>
    <div class="kpt-list-actions">
      <div class="buttons">
        <b-button tag="router-link" size="is-small" :to="{ name: 'deviceCreate' }" icon-left="plus" v-if="$hasRole('Super User,Kymeta Admin')">
          Add Device
        </b-button>
        <b-button size="is-small" icon-left="line-columns" @click="openContextPane">
          Table Settings
        </b-button>
        <b-button size="is-small" icon-left="file-export" :disabled="!(devices.length)" :title="!(devices.length) ? 'Export not available when no data is found.' : ''" @click="exportDevices">
          Export CSV
        </b-button>
        <b-button size="is-small" icon-left="sync" @click="getDevices">
          Refresh List
        </b-button>
      </div>
    </div>
    <div class="kpt-filter-bar">
      <b-input size="is-small" placeholder="Filter by serial, remote or version" v-model="nameFilter" @keyup.native.enter="addFilter({ property: 'Name', comparator: 'eq', value: nameFilter })" style="width: 200px"></b-input>
      <div class="filter-list" v-if="filterTags.length">
        <b-taglist>
          <b-tag closable @close="deleteFilter(filter)" v-for="(filter, idx) in filterTags" :key="idx"><span class="is-capitalized">{{filter.property == 'Name' ? 'Query' : filter.property}}</span> {{filter.comparator}} <strong>{{filter.value}}</strong></b-tag>
        </b-taglist>
      </div>
      <add-table-filter :add-function="addFilter" :fields="filterableColumns"></add-table-filter>
    </div>
    <div class="kpt-list-table">
      <b-table
        :data="devices"
        sticky-header
        :narrowed="true"
        :hoverable="true"
        :loading="isLoading"
        sort-icon="chevron-up"
        sort-icon-size="is-small"
        backend-sorting
        @sort="onSort"
        ref="deviceTable"
        class="has-horizontal-scroll is-full-height">
        <b-table-column 
        v-for="(column, index) in bTableColumns" :key="index"
        :field="column.field"
        :label="column.label"  
        :meta="column.meta"
        :visible="column.visible" 
        :sortable="isSortable(column.field)"
        :cell-class="cellClass(column.field)"
        >
        <template  v-slot:header="{ column }">
           {{column.label}} 
           <font-awesome-icon  :icon="['far', 'chevron-up']" v-if="sortBy==matchingSortBy(column.field) && sortDirection=='asc'"/>
           <font-awesome-icon  :icon="['far', 'chevron-down']" v-if="sortBy==matchingSortBy(column.field) && sortDirection=='desc'"/>
        </template>
        <template v-if="column.field=='serial'" v-slot="props">
          <router-link :to="{ name: 'deviceOverview', params: { id: props.row.id } }">{{props.row.serial}}</router-link>
        </template>
        <template v-else-if="column.field=='remoteName'" v-slot="props">  
          <span v-if="!props.row.remoteId">-</span>
          <router-link :to="{ name: 'remoteOverview', params: { id: props.row.remoteId } }" v-else :title="props.row.remoteName">
            {{props.row.remoteName}}
          </router-link>
        </template>
        <template v-else-if="column.field=='software.currentVersion'" v-slot="props">  
          {{ props.row.software ? props.row.software.currentVersion ? props.row.software.currentVersion : "-" : "-" }}
        </template>
        <template v-else-if="column.field=='software.targetVersion'" v-slot="props">  
          {{ props.row.software ? props.row.software.targetVersion ? props.row.software.targetVersion : "-" : "-" }}
        </template>
        <template v-else-if="column.field=='lastCheckin'" v-slot="props">  
          {{ props.row.lastCheckin ? format(new Date(props.row.lastCheckin), 'yyyy-MM-dd HH:mm') : "-" }}
        </template>
        <template v-else-if="column.field=='groupNames'" v-slot="props">  
          <span v-if="!props.row.groupIds">-</span>
          <span v-else-if="!props.row.groupIds.includes(',')">
            <router-link :to="{ name: 'groupOverview', params: { id: props.row.groupIds } }">{{props.row.groupNames}}</router-link>
          </span>
          <span v-else>
            <b-dropdown append-to-body trap-focus position="is-top-right">
              <span class="is-dash-underlined has-cursor-pointer has-text-grey" slot="trigger" role="button">
                <span>Multiple Groups</span>
              </span>
              <b-dropdown-item :focusable="false" custom paddingless>
                <ul class="kpt-sub-remote-list p-2">
                  <li v-for="group in getListOfGroups(props.row)" :key="group.id">
                    <router-link :to="{ name: 'groupOverview', params: { id: group.id } }">{{group.name}}</router-link>
                  </li>
                </ul>        
              </b-dropdown-item>
            </b-dropdown>
          </span>
        </template>
        <template v-else  :title="props.row[column.field]" v-slot="props">
          {{props.row[column.field]}}
        </template>
        </b-table-column>
        <template slot="empty">
          <p>No devices were found.</p>
        </template>
      </b-table>
    </div>
    <div class="kpt-list-footer">
      <list-pagination :total="count" :size="pageSize" :current="currentPage" @change="changePage"></list-pagination>
    </div>
  </div>
</template>
<script>
import { orderBy, flatMap } from 'lodash';
import { format } from 'date-fns';
import AddTableFilter from '../shared/AddTableFilterComponent.vue';
import ListPagination from '../shared/ListPaginationComponent.vue';
import { filterBuilderMixin } from '../shared/mixins/filterBuilder';
import { pinToDashMixin } from '../shared/mixins/pinToDash';
import { idToNameParserMixin } from '../shared/mixins/idToNameParser';
import { exportCsvMixin } from '../shared/mixins/exportCsvMixin';
import { getModelsForTypeMixin } from '../shared/mixins/getModelForDeviceTypeMixin';
import { getListOfGroupsMixin } from '../shared/mixins/getListOfGroupsMixin';

export default {
  data() {
    return {
      selectedDevices: [],
      filterTags: [],
      currentPage: 1,
      pageSize: 50,
      sortBy: 'LastCheckin',
      sortDirection: 'desc',
      nameFilter: '',
      hiddenColumns: [],
      visibleColumns: [],
      allColumns: [],
      typeSelection: [{ id: 'Antenna', name: 'Antenna' }, { id: 'Terminal', name: 'Terminal' }],
      format,
      reorderedColumns: []
    }
  },
  components: {
    AddTableFilter,
    ListPagination
  },
  mixins: [filterBuilderMixin, pinToDashMixin, idToNameParserMixin, exportCsvMixin, getModelsForTypeMixin, getListOfGroupsMixin],
  async mounted() {
    this.pageSize = this.fetchTableSetting('devices', 'pageSize');
    this.hiddenColumns = this.fetchTableSetting('devices', 'hiddenColumns') || [];
    this.reorderedColumns = this.fetchTableSetting('devices', 'reorderedColumns') || [];
    await this.getDevices();
    this.figureOutVisibleColumns();
  },
  computed: {
    devices() {
      return this.$store.state.devices.devices;
    },
    isLoading() {
      return this.$store.state.devices.devicesLoading;
    },
    title() {
      return this.$route.name;
    },
    count() {
      return this.$store.state.devices.count;
    },
    activeTableVisibleColumns() {
      return this.$store.state.system.activeTableVisibleColumns;
    },
    activeTablePageSize() {
      return this.$store.state.system.activeTablePageSize;
    },
    activeTableReorderedColumns() {
      return this.$store.state.system.activeTableReorderedColumns;
    },
    filterableColumns() {
      return this.visibleColumns.filter(v => v.filterable);
    },
    accounts() {
      return this.$store.state.accounts.accounts.map(a => ({ id: a.name, name: a.name }));
    },
    deviceTypes() {
      return this.$store.state.devices.types.map(t => ({ id: t.shortcode, name: t.name }))
    },
    allSoftwareVersions() {
      if (!this.$store.state.devices.versions) return [];
      const versions = [];
      // eslint-disable-next-line no-undef
      const software = flatMap(this.$store.state.devices.versions, (value) => value.allowedVersions);
      software.forEach(s => {
        if (!versions.includes(s.version)) versions.push(s.version);
      })
      // eslint-disable-next-line no-undef
      return orderBy(versions.map(v => ({ id: v, name: v }), ['id'], ['desc']));
    },
    bTableColumns() {
      const data = []; 
      if (this.reorderedColumns?.length > 0) { 
        this.reorderedColumns.forEach(el => { 
          data.push({
            field: el.key,
            label: el.value,
            meta: el.meta,
            visible: (this.activeTableVisibleColumns.filter(avc => avc.key == el.key)?.length > 0)
          })
        });
      } else {
        data.push({
          field: 'serial',
          label: 'Serial',
          meta: {
            field: 'Serial'
          },          
          visible: true 
        });
        data.push({
          field: 'remoteName',
          label: 'Remote',
          meta: {
            field: 'RemoteName'
          },          
          visible: true 
        });
        data.push({
          field: 'software.currentVersion',
          label: 'SW Version',
          meta: {
            field: 'Software/CurrentVersion',
            selectable: this.allSoftwareVersions
          },
          visible: true 
        });
        data.push({ 
          field: 'software.targetVersion',
          label: 'Target Version',
          meta: {
            field: 'Software/TargetVersion',
            selectable: this.allSoftwareVersions
          },          
          visible: true 
        });
        data.push({ 
          field: 'lastCheckin',
          label: 'Last Checkin',
          meta: {
            field: 'LastCheckin'
          },          
          visible: true 
        });
        data.push({ 
          field: 'accountName',
          label: 'Account',
          meta: {
            field: 'AccountName',
            selectable: this.accounts
          },          
          visible: true 
        });
        data.push({ 
          field: 'deviceModelName',
          label: 'Device Model',
          meta: {
            field: 'DeviceModelName',
            selectable: this.remapDeviceModels('DEV_ANTNA')
          },          
          visible: true 
        });
        data.push({ 
          field: 'deviceTypeName',
          label: 'Device Type',
          meta: {
            field: 'DeviceTypeName',
            selectable: this.typeSelection
          },          
          visible: true 
        });
        data.push({ 
          field: 'groupNames',
          label: 'Groups',
          meta: {
            field: 'GroupNames'
          },          
          visible: true 
        });
      }
      return data;
    }
  },
  watch: {
    activeTableVisibleColumns(newvalue) {
      this.visibleColumns = newvalue;
      this.hiddenColumns = this.allColumns.filter(d => this.visibleColumns.find(z => d.key == z.key) == null);
    },
    activeTablePageSize(newvalue) {
      this.pageSize = newvalue;
      this.currentPage = 1;
      this.getDevices();
    },
    activeTableReorderedColumns() {
      this.reorderedColumns = this.activeTableReorderedColumns;
    }
  },
  methods: {
    openContextPane() {
      this.$store.commit('setContextPane', 'columnEditor');
    },
    close() {
      this.$router.push({ path: '/' });
    },
    deleteFilter(filter) {
      this.filterTags = this.filterTags.filter(t => t.property != filter.property);
      this.getDevices();
      // KyAnalytics client plugin, event tracker
      this.$kya.event('devices', 'deleteDevicesFilter', 'Deleted devices list filter', filter);
    },
    addFilter(filter) {
      const existingFilter = this.filterTags.find(t => t.property === filter.property);
      if (existingFilter) return;
      this.filterTags.push(filter);
      this.currentPage = 1;
      this.getDevices();
      this.nameFilter = '';
      // KyAnalytics client plugin, event tracker
      this.$kya.event('devices', 'addDevicesFilter', 'Added devices list filter', filter);
    },
    changePage(page) {
      this.currentPage = page;
      this.getDevices();
    },
    onSort(field, order) {
      const currentSortColumn = this.visibleColumns.find(c => c.key == field);
      this.sortBy = currentSortColumn.meta.field;
      this.sortDirection = order;
      this.getDevices();
    },
    async getDevices() {
      const filterString = this.buildFilter(this.filterTags);
      if (!this.pageSize) this.pageSize = 50;
      await this.$store.dispatch('fetchDevices', { 
        from: (this.currentPage - 1) * this.pageSize, 
        take: this.pageSize, 
        filters: filterString, 
        sortBy: this.sortBy, 
        sortDirection: this.sortDirection 
      });
    },
    isVisible(columnName) {
      return !this.hiddenColumns.find(c => c.key == columnName);
    },
    figureOutVisibleColumns() {
      try {
        if (!this.$refs.deviceTable) return;
        this.allColumns = this.$refs.deviceTable.newColumns.map(c => ({ 
          key: c.field, 
          value: c.label, 
          meta: c.meta, 
          filterable: c.meta ? c.meta.selectable : false 
        }));
        // we have hidden columns, which means we have settings for this table
        if (this.hiddenColumns) {
          const visible = this.allColumns.filter(d => this.hiddenColumns.find(z => d.key == z.key) == null);
          this.visibleColumns = !visible.length ? this.allColumns : visible;
        }
        
        this.$store.commit('setActiveTableProperties', { 
          table: 'devices', 
          columns: this.allColumns, 
          pageSize: this.pageSize, 
          visibleColumns: this.visibleColumns,
          reorderedColumns: this.activeTableReorderedColumns
        });
      } catch (ex) {
        console.error('Error encountered while attempting to figure out visible columns.', ex);
      }
    },
    fetchTableSetting(tableName, settingName) {
      const table = this.$store.state.user.settings.userListSettings.find(r => r.table == tableName);
      return table ? table[settingName] : null;
    },
    async remapDevicesForExport() {
      const visibleColumns = this.activeTableVisibleColumns.map(c => c.key); 
      const payload = {
        from: 0,
        take: this.count,
        filters: this.buildFilter(this.filterTags),
        sortBy: this.sortBy,
        sortDirection: this.sortDirection,
        bypassState: true
      };
      const data = await this.$store.dispatch('fetchDevices', payload);
      // check for any errors
      if (data.isError) {
        // check for any errors
        this.$buefy.toast.open({
          duration: 3000,
          message: data.ex.message,
          type: 'is-danger'
        });
        return null;
      }
      const devices = data.payload;
      const dataToExport = [];
      if (!!devices && !!(devices.length)) {
        devices.forEach(device => {
          const item = [];
          visibleColumns.forEach(col => {
            const isColVisible = visibleColumns.indexOf(col) > -1;
            if (col.includes('lastCheckin')) { 
              item.push(device[col] && isColVisible ? (format(new Date(device[col]), ' yyyy-MM-dd HH:mm')) : ''); 
            } else if (col.includes('software')) { 
              item.push(device?.software && isColVisible ? device?.software?.[this.colEle2(col)] : '');  
            } else {
              item.push(isColVisible ? device[col] : '');
            }
          });
          dataToExport.push(item);
        });
      } 
      return dataToExport;
    },
    remapDeviceHeadersForExport() {
      return this.activeTableVisibleColumns.map(c => c.value);
    },
    remapDeviceModels(type) {
      return this.getModelsForType(type, this.$store).map(t => ({ id: t.name, name: t.name }));
    },
    colEle2(col) {
      if (col) {
        const splitArray = col.split('.');
        if (splitArray.length == 2) return splitArray[1];
      }
      return '';
    },
    async exportDevices() {
      const headers = await this.remapDeviceHeadersForExport();
      const devices = await this.remapDevicesForExport();
      if (!devices) return;
      this.exportCsvArray(headers, devices, 'devices');
      // KyAnalytics client plugin, event tracker
      this.$kya.event('devices', 'exportDevicesReport', 'Export devices list');
      // eslint-disable-next-line consistent-return
      return true;
    },
    isSortable(columnName) {   
      if (columnName == 'software.currentVersion' || columnName == 'software.targetVersion' || columnName == 'groupNames') return false;
      return true;
    },
    cellClass(columnName) {
      if (columnName == 'software.currentVersion' || columnName == 'software.targetVersion') return 'is-200';
      if (columnName == 'accountName' || columnName == 'deviceModelName' || columnName == 'groupNames') return 'is-200 truncate';
      if (columnName == 'lastCheckin' || columnName == 'deviceTypeName') return 'is-250';
      if (columnName == 'serial') return 'is-400';
      if (columnName == 'remoteName') return 'truncate';
      return '';
    },
    matchingSortBy(columnName) {
      return columnName.charAt(0).toUpperCase() + columnName.slice(1);
    }
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('setContextPane', null);
    next();
  }
}
</script>
<style lang="scss" scoped>
.kpt-devices-root {
  position: relative;
  height: 100%;

  ::v-deep .loading-overlay {
    z-index: 9;
  }
}
</style>
